import type { CVData } from "../types/cv";
import { PHASES, ROLE_LABELS } from "./labels";
import { calculateAge, formatPeriod } from "./utils";

export const AI_VERSION_PATH = "/cv.md";

const oneLine = (text: string) => text.replace(/\s*\n\s*/g, " ").trim();

const field = (label: string, value: string) => `- ${label}: ${oneLine(value)}`;

const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export function renderCvMarkdown(cv: CVData, today = new Date()): string {
  const { personal_info: info } = cv;

  const lines: string[] = [
    `# ${info.name} 職務経歴書`,
    "",
    `> ${isoDate(today)} 現在。Web 版: ${info.links.resume}`,
    "",
    "## 基本情報",
    "",
    field("所属", info.affiliation),
    field("年齢", calculateAge(info.birthday, today)),
    field("性別", info.gender),
    field("最寄駅", info.nearest_station),
    field("学歴", info.education),
    field("資格", info.qualifications),
    field("役割", info.role),
    field("専門分野", info.expertise),
    `- リンク: [GitHub](${info.links.github}) / [Facebook](${info.links.facebook}) / [Web版](${info.links.resume})`,
    "",
    "## 自己PR",
    "",
    cv.self_pr.trim(),
    "",
    "## 職歴",
  ];

  for (const experience of cv.history) {
    lines.push(
      "",
      `### ${formatPeriod(experience.period)} ${experience.company}`,
      "",
      field("役職", experience.role),
      field("雇用形態", experience.contract_type),
      field("業務内容", experience.duties),
    );

    for (const project of experience.projects) {
      const technologies = [
        ...project.technologies.languages,
        ...project.technologies.frameworks,
        ...project.technologies.database,
        ...project.technologies.os,
      ];
      const phases = PHASES.filter(([key]) => project.phases[key]).map(
        ([, label]) => label,
      );
      const roles = Object.entries(project.team.roles);
      const teamTotal = roles.reduce((total, [, count]) => total + count, 0);
      const teamBreakdown = roles
        .map(([role, count]) => `${ROLE_LABELS[role] ?? role} ${count}人`)
        .join("、");

      lines.push(
        "",
        `#### ${formatPeriod(project.period)} ${project.title}`,
        "",
        field("技術", technologies.join(", ")),
      );
      if (phases.length > 0) lines.push(field("フェーズ", phases.join("、")));
      lines.push(field("担当業務", project.responsibilities.tasks.join("、")));
      if (project.responsibilities.comment) {
        lines.push(field("詳細", project.responsibilities.comment));
      }
      lines.push(
        field("課題", project.challenges),
        field("解決アクション", project.actions),
        field("実績", project.achievements),
        field("チーム", `${teamTotal}人（${teamBreakdown}）`),
      );
    }
  }

  lines.push("", "## 習得技術（個人開発）", "");
  for (const group of cv.personal_development.technologies) {
    lines.push(field(group.category, group.items.join(", ")));
  }

  return `${lines.join("\n")}\n`;
}
